import { useTranslations } from '@vocab/react';
import {
  Button,
  IconSettings,
  MenuRenderer,
  MenuItem,
  MenuItemDivider,
  useResponsiveValue,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import { useEffect, useState, type FC } from 'react';

import type { IJob } from '../../../../stores/JobStore/Job';
import type { JobAction } from '../actions';
import { useJobActionOptions } from '../hooks/useJobActionOptions';

import translations from './.vocab';

interface JobActionMenuProps {
  job: IJob;
  onAction: (jobAction: JobAction) => void;
  canExportApplicationsToCsv: boolean;
  isIndirectJobButCanBeChanged: boolean;
}

const JobActionMenu: FC<JobActionMenuProps> = ({
  job,
  onAction,
  canExportApplicationsToCsv,
  isIndirectJobButCanBeChanged,
}) => {
  const [isReadyOnClient, setIsReadyOnClient] = useState(false);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });
  useEffect(() => {
    setIsReadyOnClient(true);
  }, []);

  const { t } = useTranslations(translations);
  const [jobActionOptions, jobShareActionOptions, jobConfigOptions] =
    useJobActionOptions({
      job,
      showExportToCsv: canExportApplicationsToCsv,
      isIndirectJobButCanBeChanged,
    });

  if (!isReadyOnClient) {
    return null;
  }

  return (
    <MenuRenderer
      offsetSpace={isMobile ? 'xxsmall' : 'xsmall'}
      align={isMobile ? 'left' : 'right'}
      placement="bottom"
      trigger={(triggerProps) => (
        <Button
          data={{
            intercomid: 'manageJob',
            testid: 'job-action-dropdown',
          }}
          size="small"
          tone="neutral"
          variant="soft"
          icon={<IconSettings />}
          {...triggerProps}
        >
          {t('Manage job')}
        </Button>
      )}
    >
      {jobActionOptions
        .filter((option) => !option.disabled)
        .map((option) => (
          <MenuItem
            key={option.label}
            icon={option.icon}
            onClick={() => onAction(option.value as JobAction)}
          >
            {option.label}
          </MenuItem>
        ))}
      {jobConfigOptions.filter((options) => !options.disabled).length > 0 && (
        <MenuItemDivider />
      )}
      {jobConfigOptions
        .filter((option) => !option.disabled)
        .map((option) => (
          <MenuItem
            key={option.label}
            icon={option.icon}
            onClick={() => onAction(option.value as JobAction)}
          >
            {option.label}
          </MenuItem>
        ))}
      {jobShareActionOptions.filter((options) => !options.disabled).length >
        0 && <MenuItemDivider />}
      {jobShareActionOptions
        .filter((option) => !option.disabled)
        .map((option) => (
          <MenuItem
            key={option.label}
            icon={option.icon}
            onClick={() => onAction(option.value as JobAction)}
          >
            {option.label}
          </MenuItem>
        ))}
    </MenuRenderer>
  );
};

export default observer(JobActionMenu);
